import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { SelectOptions } from 'src/shared/models';

@Component({
  selector: 'atom-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputAutocompleteComponent),
      multi: true
    }
  ]
})
export class InputAutocompleteComponent implements OnInit, ControlValueAccessor, OnChanges, OnDestroy {
  @Input() title: string = '';
  @Input() optionsList: SelectOptions[] = [];
  @Input() disabled: boolean = false;
  @Output() changed = new EventEmitter<string>();
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  value = new FormControl<SelectOptions | null>(null);
  translatedTitle: string = '';
  filteredOptions: Observable<SelectOptions[]> = new Observable<SelectOptions[]>();

  private langChangeSubscription?: Subscription;
  private propagateTouched: () => void = () => {};
  private propagateChange: (value: string) => void = () => {};

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateTitleTranslation();
    });

    this.initializeFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['optionsList']) {
      this.initializeFilter();
    }
  }

  writeValue(value: string | null): void {
    if (!value) {
      this.value.setValue(null, { emitEvent: false });

      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.writeValue(null);
      }

      if (this.autocompleteTrigger && this.autocompleteTrigger.autocomplete) {
        // Deseleccionar cualquier opción seleccionada en el autocomplete
        this.autocompleteTrigger.autocomplete.options.forEach(option => {
          if (option.selected) {
            option.deselect();
          }
        });
      }

      this.initializeFilter();
      return;
    }

    const match = this.optionsList.find(opt => opt.value === value);
    this.value.setValue(match || null, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedOption: SelectOptions = event.option.value;
    this.value.setValue(selectedOption);
    this.propagateChange(selectedOption.value);
    this.changed.emit(selectedOption.value);
  }

  onBlur() {
    this.propagateTouched();
  }

  displayFn(option: SelectOptions | null): string {
    if (!option) return '';
    return this.translateText(option.title);
  }

  getOptionText(option: SelectOptions): string {
    return this.translateText(option.title);
  }

  private translateText(text: string): string {
    if (!text) return '';

    if (text.includes('|')) {
      // Si el texto contiene un pipe, se asume formato "key|{param1: value1, param2: value2}"
      const [key, paramsStr] = text.split('|');
      if (paramsStr) {
        try {
          const params = JSON.parse(paramsStr.trim());
          return this.translateService.instant(key.trim(), params);
        } catch (e) {
          console.warn('Error parseando parámetros de traducción:', e);
          return this.translateService.instant(key.trim());
        }
      }
      return this.translateService.instant(key.trim());
    }

    if (!isNaN(Number(text)) || /\d|%/.test(text)) {
      return text;
    }

    const translation = this.translateService.instant(text);
    return translation === text ? text : translation;
  }

  private updateTitleTranslation() {
    if (this.title) {
      const translation = this.translateService.instant(this.title);
      this.translatedTitle =
        translation === this.title ? this.title : translation;
    }
  }

  private initializeFilter() {
    this.filteredOptions = this.value.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterText = typeof value === 'string' ? value : value?.title || '';
        return this._filter(filterText);
      }),
    );
  }

  private _filter(value: string): SelectOptions[] {
    const filterValue = value.toLowerCase();

    return this.optionsList.filter(option => option.title.toLowerCase().includes(filterValue));
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
